/**
 * Graph view of the gated scoring pipeline.
 *
 * Layout: four columns — TRIAGE (chips for tags/flags/verdict) → SELECTED
 * specialists (grouped by selection reason) → FINAL verdict → SKIPPED
 * (faded, right-hand). SVG edges connect:
 *   - each triage tag/flag chip → its triggering specialist
 *   - each specialist → the final verdict
 *
 * SVG edges are positioned by measuring node centers after render, so
 * the column widths / heights can flow with the content without
 * needing a layout engine. One-time measurement on mount + a
 * `ResizeObserver` keep edges aligned when the container reflows.
 *
 * When triage short-circuits, the middle two columns grey out and a
 * single "zap" edge jumps from triage to final.
 */
import React, {useLayoutEffect, useRef, useState} from 'react';
import {Box, Chip, Paper, Tooltip, Typography} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import {AgentScore, EvalAgentEntry, SpecialistSelectionReason} from '../../../types/generated/graphql';
import {pcolors, cardSx, sectionLabelSx} from '../../../theme/planapusTheme';
import {humanizeSpecialization} from '../../Admin/agentLabels';
import {agentScoreTone, humanizeTag, humanizeFlag, verdictTone, useChainData} from './useChainData';

interface Props {
  scores: AgentScore[];
  agents: EvalAgentEntry[];
  childAge?: number;
}

// Edges connect DOM nodes by id. Each group (tag chip, flag chip,
// specialist row, final, triage) registers itself with a known id,
// and the graph's SVG overlay draws curves between them.
const NODE = {
  triage: 'graph-node-triage',
  tag: (t: string) => `graph-node-tag-${t}`,
  flag: (f: string) => `graph-node-flag-${f}`,
  specialist: (spec: string) => `graph-node-spec-${spec}`,
  final: 'graph-node-final',
} as const;

type Edge = {from: string; to: string; kind: 'trigger' | 'to-final' | 'short-circuit'};

export function ScoringChainGraph({scores, agents, childAge = 10}: Props) {
  const {
    triage,
    final,
    specialistScores: specialists,
    skipped,
    didGate,
    shortCircuited,
    channelGateScore,
  } = useChainData(scores, agents, childAge);

  // IMPORTANT: every hook below must run on every render, in the same
  // order — no early `return` allowed between here and the end of the
  // hook list. The channel-gate branch happens *after* the hooks.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [edgeCoords, setEdgeCoords] = useState<
    {d: string; kind: Edge['kind']; key: string}[]
  >([]);

  // Compute edges from data: every triage tag/flag → each specialist whose
  // triggers include it; every specialist → the final node.
  const edges: Edge[] = [];
  if (didGate && !shortCircuited) {
    for (const s of specialists) {
      const ctx = s.gatedContext;
      if (!ctx) {
        // Legacy stage-1 specialists — fall back to a plain from-triage edge
        // so the graph still shows them connected to something.
        edges.push({from: NODE.triage, to: NODE.specialist(String(s.agent.specialization)), kind: 'trigger'});
      } else if (ctx.selectionReason === SpecialistSelectionReason.CategoryTag && ctx.selectionDetail) {
        edges.push({
          from: NODE.tag(ctx.selectionDetail),
          to: NODE.specialist(String(s.agent.specialization)),
          kind: 'trigger',
        });
      } else if (ctx.selectionReason === SpecialistSelectionReason.RiskFlag && ctx.selectionDetail) {
        edges.push({
          from: NODE.flag(ctx.selectionDetail),
          to: NODE.specialist(String(s.agent.specialization)),
          kind: 'trigger',
        });
      } else {
        // Always-on: draw from the triage node itself.
        edges.push({from: NODE.triage, to: NODE.specialist(String(s.agent.specialization)), kind: 'trigger'});
      }
      if (final) {
        edges.push({
          from: NODE.specialist(String(s.agent.specialization)),
          to: NODE.final,
          kind: 'to-final',
        });
      }
    }
  }
  if (shortCircuited) {
    edges.push({from: NODE.triage, to: NODE.final, kind: 'short-circuit'});
  }

  // Recompute edge coordinates after every layout — nodes may reflow as
  // content changes, and a ResizeObserver keeps edges aligned when the
  // container itself resizes (window width, panel expand/collapse).
  useLayoutEffect(() => {
    const recalc = () => {
      const root = containerRef.current;
      if (!root) return;
      const origin = root.getBoundingClientRect();
      const pts: {d: string; kind: Edge['kind']; key: string}[] = [];
      for (const e of edges) {
        const fromEl = document.getElementById(e.from);
        const toEl = document.getElementById(e.to);
        if (!fromEl || !toEl) continue;
        const a = fromEl.getBoundingClientRect();
        const b = toEl.getBoundingClientRect();
        const x1 = a.right - origin.left;
        const y1 = a.top + a.height / 2 - origin.top;
        const x2 = b.left - origin.left;
        const y2 = b.top + b.height / 2 - origin.top;
        const cx = (x1 + x2) / 2;
        // Cubic bezier for a smooth curve — control points lift horizontally
        // so the curve doesn't cross neighboring columns.
        pts.push({
          d: `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`,
          kind: e.kind,
          key: `${e.from}->${e.to}`,
        });
      }
      setEdgeCoords(pts);
    };
    recalc();
    const ro = new ResizeObserver(recalc);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', recalc);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recalc);
    };
    // `edges` is derived from scores/agents — re-running on every
    // render is fine here because the DOM layout is idempotent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scores, agents, didGate, shortCircuited]);

  if (channelGateScore) {
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 1.5,
          bgcolor: pcolors.red[50],
          border: `1px solid ${pcolors.red[100]}`,
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
          <BoltIcon sx={{fontSize: 20, color: pcolors.red[700]}} />
          <Typography sx={{fontSize: 14, fontWeight: 700, color: pcolors.red[700]}}>
            Channel gate — scoring skipped
          </Typography>
        </Box>
        <Typography sx={{fontSize: 12, color: pcolors.textPrimary, lineHeight: 1.55}}>
          {channelGateScore.explanation}
        </Typography>
        <Typography sx={{fontSize: 11, color: pcolors.textMuted, mt: 1}}>
          No LLM calls were made. The parent channel was already conclusively
          restricted by prior scoring, so triage → specialists → finalize were
          all bypassed.
        </Typography>
      </Box>
    );
  }

  if (!didGate) {
    return (
      <Box sx={{p: 3, textAlign: 'center'}}>
        <Typography sx={{fontSize: 12, color: pcolors.textMuted}}>
          No triage row recorded — graph view isn't available for legacy (pre-gated) scores.
        </Typography>
      </Box>
    );
  }

  const dim = shortCircuited ? 0.4 : 1;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        minHeight: 320,
        display: 'grid',
        gridTemplateColumns: '240px 1fr 220px 180px',
        gap: 2,
        alignItems: 'start',
      }}
    >
      {/* SVG edge overlay — absolutely positioned under the nodes. */}
      <Box
        component="svg"
        sx={{position: 'absolute', inset: 0, pointerEvents: 'none'}}
        aria-hidden
      >
        {edgeCoords.map(ec => (
          <path
            key={ec.key}
            d={ec.d}
            fill="none"
            stroke={
              ec.kind === 'short-circuit'
                ? pcolors.amber[500]
                : ec.kind === 'to-final'
                  ? pcolors.blue[500]
                  : pcolors.textMuted
            }
            strokeWidth={ec.kind === 'short-circuit' ? 2.5 : 1.5}
            strokeOpacity={ec.kind === 'short-circuit' ? 1 : 0.6}
            strokeDasharray={ec.kind === 'to-final' ? '0' : '4 4'}
          />
        ))}
      </Box>

      {/* Column 1 — triage node + tag/flag chips */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, zIndex: 1}}>
        <Typography sx={sectionLabelSx}>Triage</Typography>
        <Paper
          id={NODE.triage}
          elevation={0}
          sx={{
            ...cardSx,
            p: 1.25,
            bgcolor: verdictTone(triage?.triageOutput?.verdict).bg,
            borderColor: verdictTone(triage?.triageOutput?.verdict).fg,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: verdictTone(triage?.triageOutput?.verdict).fg,
              textTransform: 'capitalize',
            }}
          >
            {verdictTone(triage?.triageOutput?.verdict).label}
          </Typography>
          {triage?.triageOutput?.confidence != null && (
            <Typography sx={{fontSize: 11, color: pcolors.textSecondary, mt: 0.25, fontVariantNumeric: 'tabular-nums'}}>
              confidence {(triage.triageOutput.confidence * 100).toFixed(0)}%
            </Typography>
          )}
        </Paper>

        {!!triage?.triageOutput?.categoryTags?.length && (
          <Box>
            <Typography sx={{fontSize: 10, color: pcolors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5}}>
              categories
            </Typography>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
              {triage.triageOutput.categoryTags.map(t => (
                <Chip
                  id={NODE.tag(t)}
                  key={t}
                  size="small"
                  label={humanizeTag(t)}
                  sx={{fontSize: 11, bgcolor: pcolors.blue[50], color: pcolors.blue[700], justifyContent: 'flex-start'}}
                />
              ))}
            </Box>
          </Box>
        )}

        {!!triage?.triageOutput?.riskFlags?.length && (
          <Box>
            <Typography sx={{fontSize: 10, color: pcolors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5}}>
              flags
            </Typography>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
              {triage.triageOutput.riskFlags.map(f => (
                <Chip
                  id={NODE.flag(f)}
                  key={f}
                  size="small"
                  label={humanizeFlag(f)}
                  sx={{fontSize: 11, bgcolor: pcolors.amber[50], color: pcolors.amber[700], justifyContent: 'flex-start'}}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Column 2 — specialists that ran */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, zIndex: 1, opacity: dim}}>
        <Typography sx={sectionLabelSx}>Specialists that ran</Typography>
        {specialists.length === 0 ? (
          <Typography sx={{fontSize: 12, color: pcolors.textMuted}}>
            {shortCircuited ? 'Skipped — triage short-circuited.' : 'None.'}
          </Typography>
        ) : (
          specialists.map(s => <SpecialistNode key={s.id} score={s} />)
        )}
      </Box>

      {/* Column 3 — final verdict */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, zIndex: 1}}>
        <Typography sx={sectionLabelSx}>Final</Typography>
        {(() => {
          const finalTone = final
            ? agentScoreTone(final)
            : {bg: pcolors.borderLight, fg: pcolors.border, strong: pcolors.border};
          return (
            <Paper
              id={NODE.final}
              elevation={0}
              sx={{
                ...cardSx,
                p: 1.25,
                textAlign: 'center',
                bgcolor: finalTone.bg,
                borderColor: finalTone.fg,
              }}
            >
              {final ? (
                <>
                  <Typography
                    sx={{fontSize: 20, fontWeight: 800, color: finalTone.fg, fontVariantNumeric: 'tabular-nums'}}
                  >
                    {final.score.toFixed(1)}
                  </Typography>
                  <Typography sx={{fontSize: 11, color: finalTone.fg, textTransform: 'capitalize'}}>
                    {final.rating.toLowerCase()}
                  </Typography>
                </>
              ) : shortCircuited ? (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5}}>
                  <BoltIcon sx={{fontSize: 18, color: pcolors.amber[700]}} />
                  <Typography sx={{fontSize: 12, color: pcolors.amber[700]}}>short-circuit</Typography>
                </Box>
              ) : (
                <Typography sx={{fontSize: 11, color: pcolors.textMuted}}>pending</Typography>
              )}
            </Paper>
          );
        })()}
      </Box>

      {/* Column 4 — skipped specialists */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, zIndex: 1, opacity: 0.55}}>
        <Typography sx={sectionLabelSx}>Skipped ({skipped.length})</Typography>
        {skipped.length === 0 ? (
          <Typography sx={{fontSize: 11, color: pcolors.textMuted}}>—</Typography>
        ) : (
          skipped.map(a => (
            <Tooltip
              key={`${a.specialization}-${a.version}`}
              title={skipReason(a)}
              placement="left"
            >
              <Typography
                sx={{
                  fontSize: 11,
                  color: pcolors.textSecondary,
                  py: 0.25,
                  borderBottom: `1px dashed ${pcolors.borderLight}`,
                }}
                noWrap
              >
                {humanizeSpecialization(a.specialization)}
              </Typography>
            </Tooltip>
          ))
        )}
      </Box>
    </Box>
  );
}

function SpecialistNode({score}: {score: AgentScore}) {
  const tone = agentScoreTone(score);
  return (
    <Paper
      id={`graph-node-spec-${String(score.agent.specialization)}`}
      elevation={0}
      sx={{
        ...cardSx,
        p: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        borderLeft: `3px solid ${tone.fg}`,
      }}
    >
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography sx={{fontSize: 12, fontWeight: 600, color: pcolors.textPrimary}} noWrap>
          {humanizeSpecialization(score.agent.specialization)}
        </Typography>
      </Box>
      <Chip
        size="small"
        label={score.score.toFixed(1)}
        sx={{
          fontSize: 11,
          fontWeight: 700,
          bgcolor: tone.bg,
          color: tone.fg,
          fontVariantNumeric: 'tabular-nums',
        }}
      />
    </Paper>
  );
}

function skipReason(a: EvalAgentEntry): string {
  const t = a.triggers;
  if (!t) return 'not triggered';
  const tags = t.categoryTags ?? [];
  const flags = t.riskFlags ?? [];
  if (tags.length && flags.length) {
    return `needs category [${tags.map(humanizeTag).join(', ')}] or flag [${flags.map(humanizeFlag).join(', ')}]`;
  }
  if (tags.length) return `needs category [${tags.map(humanizeTag).join(', ')}]`;
  if (flags.length) return `needs flag [${flags.map(humanizeFlag).join(', ')}]`;
  return 'not triggered';
}
